import { useEffect, useState } from 'react';

/** How often an open, visible tab checks for a new deployment. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Minimum gap between checks when the tab regains focus. */
const MIN_FOCUS_CHECK_GAP_MS = 60 * 1000;

/**
 * Returns the sorted, de-duplicated list of Backstage build asset paths
 * (scripts and stylesheets under `/static/`) referenced by a document.
 *
 * Backstage names these files with a content hash (for example
 * `/static/main.8ad5b884.js`), so the list only changes when the frontend
 * code itself changes. That makes it a reliable "frontend version" without
 * any extra build or CI step.
 */
function collectAssetPaths(root: ParentNode): string[] {
  const paths = new Set<string>();
  root
    .querySelectorAll('script[src], link[rel="stylesheet"][href]')
    .forEach(element => {
      const raw = element.getAttribute('src') ?? element.getAttribute('href');
      if (!raw) {
        return;
      }
      const { pathname } = new URL(raw, document.baseURI);
      if (pathname.includes('/static/')) {
        paths.add(pathname);
      }
    });
  return Array.from(paths).sort();
}

/**
 * Captured once, when this module is first evaluated by the main bundle.
 * At that point the document only contains the tags from the original
 * index.html, not scripts that webpack adds later when lazy-loading chunks.
 */
const loadedAssetPaths = collectAssetPaths(document);

function sameAssets(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

async function fetchLatestAssetPaths(): Promise<string[] | undefined> {
  // index.html is served with `no-store`, and `cache: 'no-store'` makes sure
  // nothing between the browser and the server answers with an old copy.
  const response = await fetch(document.baseURI, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });
  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok || !contentType.includes('text/html')) {
    return undefined;
  }
  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return collectAssetPaths(parsed);
}

/**
 * Reports whether a newer frontend build than the one currently running in
 * this tab has been deployed. Checks periodically while the tab is visible
 * and again whenever the tab becomes visible after being in the background.
 */
export function useNewVersionAvailable(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    // Dev server assets are not content hashed, and there is nothing to
    // compare against if the page has no build assets.
    if (
      process.env.NODE_ENV !== 'production' ||
      loadedAssetPaths.length === 0
    ) {
      return undefined;
    }

    let stopped = false;
    let lastCheck = 0;

    const check = async () => {
      if (stopped || document.visibilityState !== 'visible') {
        return;
      }
      lastCheck = Date.now();
      try {
        const latest = await fetchLatestAssetPaths();
        // Ignore failed or unexpected responses (offline, login redirect,
        // maintenance page): only a successful, different index.html counts.
        if (!stopped && latest && latest.length > 0) {
          if (!sameAssets(latest, loadedAssetPaths)) {
            stopped = true;
            setAvailable(true);
          }
        }
      } catch {
        // Network errors are expected occasionally; try again next cycle.
      }
    };

    const onVisibilityChange = () => {
      if (Date.now() - lastCheck >= MIN_FOCUS_CHECK_GAP_MS) {
        check();
      }
    };

    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return available;
}
